import {
  TrendingUp, Wrench, Users, Package, BookOpen,
  HeadphonesIcon, Brain, FolderKanban, Link2,
} from "lucide-react";

export const SLUG_TO_NAME = {
  "sales-flow": "SalesFlow", ops: "Ops", teams: "Teams", stockroom: "Stockroom",
  bookkeep: "Bookkeep", support: "Support", projects: "Projects", connect: "Connect",
  cerebrum: "Cerebrum",
};

export const MODULE_COUNT = 8; // Cerebrum is an add-on, not counted as a module

export const MODULE_DATA = {
  "sales-flow": {
    name: "Rogue SalesFlow",
    tagline: "From suspect to signed deal. Every step tracked, every insight surfaced, every follow-up scheduled.",
    icon: TrendingUp,
    heroDescription: "SalesFlow isn't just a CRM — it's a complete revenue engine. Track suspects from first identification through to signed customer. Deep-research every prospect with AI. Plan multi-stop sales trips with route optimisation. Visualise your funnel in real time. Manage quotes, invoices, and commissions. All from your phone or desktop.",
    gallery: [
      { src: "/screenshots/dark-leads.png", label: "Lead pipeline — dark mode", mode: "dark" },
      { src: "/screenshots/light-leads.png", label: "Lead pipeline — light mode", mode: "light" },
      { src: "/screenshots/dark-salesfunnel.png", label: "Sales funnel analytics", mode: "dark" },
      { src: "/screenshots/light-salesfunnel.png", label: "Sales funnel — light mode", mode: "light" },
      { src: "/screenshots/dark-suspects.png", label: "Suspect tracking", mode: "dark" },
      { src: "/screenshots/dark-research.png", label: "AI deep research", mode: "dark" },
      { src: "/screenshots/light-research.png", label: "Research — light mode", mode: "light" },
      { src: "/screenshots/dark-trips.png", label: "Sales trip planner", mode: "dark" },
      { src: "/screenshots/light-trips.png", label: "Trips — light mode", mode: "light" },
      { src: "/screenshots/dark-map.png", label: "Customer map view", mode: "dark" },
      { src: "/screenshots/light-map.png", label: "Map — light mode", mode: "light" },
      { src: "/screenshots/dark-customers.png", label: "Customer 360 view", mode: "dark" },
      { src: "/screenshots/dark-followups.png", label: "Follow-up queue", mode: "dark" },
      { src: "/screenshots/dark-analytics.png", label: "Revenue analytics", mode: "dark" },
      { src: "/screenshots/mobile-leads.png", label: "Leads — mobile", mode: "mobile" },
      { src: "/screenshots/mobile-map.png", label: "Map — mobile", mode: "mobile" },
    ],
    features: [
      {
        badge: "Suspect Tracking",
        title: "Identify, score, and qualify suspects before they even know you exist",
        description: "Every potential customer starts as a suspect. Log them from industry events, referrals, website visits, or AI intake. Cerebrum auto-enriches every suspect with company data, industry classification, star ratings, and a hot-prospect score — so your team focuses on the ones most likely to convert.",
        bullets: [
          "Suspect database with auto-enrichment (company size, industry, location)",
          "Hot-prospect scoring based on engagement signals and fit criteria",
          "Bulk import from CSV, spreadsheets, or manual entry",
          "Promote suspects to leads with one click — all data carries over",
          "Industry heat-map showing where your best opportunities cluster",
        ],
        screenshots: [
          { src: "/screenshots/dark-suspects.png", caption: "Suspect tracking" },
        ],
      },
      {
        badge: "AI Deep Research",
        title: "Cerebrum researches every prospect — 30+ data points in seconds",
        description: "When a lead enters the pipeline, Cerebrum builds a full research brief: company overview, website analysis, Google Places data, pricing intelligence, competitor landscape, and Wayback Machine history. Your reps walk into every call armed with insights that took AI seconds and would take a human hours.",
        bullets: [
          "Automated company research from 6+ data sources",
          "Google Places integration — reviews, ratings, photos, opening hours",
          "Website scraping and content analysis",
          "Competitor detection and market positioning",
          "Pricing intelligence extraction from public sources",
          "Wayback Machine analysis showing company history and growth",
          "Downloadable PDF research briefs for offline use",
        ],
        screenshots: [
          { src: "/screenshots/dark-research.png", caption: "AI research briefs" },
          { src: "/screenshots/light-research.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Lead Pipeline",
        title: "Track every lead through your entire sales cycle",
        description: "Custom pipeline stages, drag-and-drop reordering, phase tracking, and conversion metrics. Every lead has a full timeline showing calls, meetings, comments, quotes, and follow-ups. Convert qualified leads directly into job cards or customers without re-entering data.",
        bullets: [
          "Custom pipeline phases (New → Contacted → Qualified → Proposal → Won/Lost)",
          "Lead onboarding forms with service interest capture",
          "Call logging with outcome tracking and next-step scheduling",
          "Meeting scheduling with outcome capture and comment threads",
          "Direct conversion: Lead → Customer or Lead → Job Card",
          "Bulk import with AI-powered field mapping",
        ],
        screenshots: [
          { src: "/screenshots/dark-leads.png", caption: "Lead pipeline" },
          { src: "/screenshots/light-leads.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Sales Funnel",
        title: "Visualise your entire funnel — spot bottlenecks before they cost you deals",
        description: "The sales funnel shows conversion rates at every stage, deal velocity, and revenue forecasting. Filter by rep, product, date range, or region. Drill into any stage to see the deals inside. Lost-deal analysis tells you why they dropped off.",
        bullets: [
          "Visual funnel with conversion rates per stage",
          "Revenue forecasting based on pipeline velocity",
          "Rep-level performance comparison",
          "Lost-deal analysis with categorised reasons",
          "Suspect → Lead → Customer timeline view",
        ],
        screenshots: [
          { src: "/screenshots/dark-salesfunnel.png", caption: "Sales funnel" },
          { src: "/screenshots/light-salesfunnel.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Trip Planning",
        title: "Plan multi-stop sales trips with AI-optimised routes",
        description: "Planning a week of client visits? Rogue's trip planner lets you add stops from your customer and lead databases, optimise the route, estimate drive times, and compile a full itinerary. Deep trip mode generates a sales prep pack with customer research, talking points, and follow-up templates.",
        bullets: [
          "Multi-stop trip creation with route optimisation (OSRM)",
          "Drive time estimation between stops",
          "AI-compiled deep itinerary with per-stop research and talking points",
          "Meeting overlap detection across your team",
          "Shareable trip links for team coordination",
          "Post-trip debrief with AI-guided reflection",
        ],
        screenshots: [
          { src: "/screenshots/dark-trips.png", caption: "Trip planner" },
          { src: "/screenshots/light-trips.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Map View",
        title: "See all your customers and leads on a live map",
        description: "Every customer and lead with an address is pinned on an interactive map. Filter by status, industry, or assigned rep. Click any pin to see details, navigate directly, or start a call. Plan your day by geography, not by spreadsheet.",
        bullets: [
          "Interactive map with customer and lead pins",
          "Filter by status, industry, rep assignment",
          "One-tap navigation to any customer location",
          "Cluster view for dense areas",
          "GPS-verified addresses from Google Places",
        ],
        screenshots: [
          { src: "/screenshots/dark-map.png", caption: "Map view" },
          { src: "/screenshots/mobile-map.png", caption: "Mobile", mobile: true },
        ],
      },
      {
        badge: "Follow-ups & Calls",
        title: "Never let a warm lead go cold — every promise tracked",
        description: "Every follow-up your team schedules is tracked with dates, priorities, and outcomes. Overdue follow-ups get flagged and pushed to mobile. Call logs capture duration, outcome, and next steps. Cerebrum suggests what to say based on conversation history.",
        bullets: [
          "Automated follow-up scheduling with reminders",
          "Overdue follow-up alerts via push and inbox",
          "Call logging with outcome categories",
          "Meeting scheduling with calendar sync",
          "AI-drafted follow-up messages based on context",
        ],
        screenshots: [
          { src: "/screenshots/dark-followups.png", caption: "Follow-up queue" },
        ],
      },
      {
        badge: "Quotes & Documents",
        title: "Generate quotes, invoices, and sales documents — branded to your business",
        description: "Create professional quotes and invoices directly from lead or customer records. Track payment status, send reminders, and generate PDF downloads. Sales documents link back to the lead, project, and job card for full traceability.",
        bullets: [
          "Quote and invoice generation with PDF export",
          "Payment status tracking (pending, paid, overdue)",
          "Customer portal access for quote approval and payment",
          "Line-item management with pricing calculations",
        ],
        screenshots: [
          { src: "/screenshots/dark-customers.png", caption: "Customer documents" },
          { src: "/screenshots/light-customers.png", caption: "Light mode" },
        ],
      },
    ],
    cerebrum: "Cerebrum powers SalesFlow at every stage: it researches suspects from 6+ data sources, scores hot-prospect fit, drafts follow-up emails, compiles multi-stop trip itineraries with per-customer talking points, annotates your funnel with conversion insights, and flags dormant customers before they churn. It's not an add-on — it's the reason SalesFlow outperforms traditional CRMs.",
    crossSell: ["ops", "bookkeep", "support"],
    pricing: { starter: 99, pro: 149, business: 199 },
  },

  ops: {
    name: "Rogue Ops",
    tagline: "Your field team's command centre. Schedule, dispatch, track, photograph, sign off — all from a phone.",
    icon: Wrench,
    heroDescription: "Ops replaces your paper job cards, your WhatsApp scheduling, and your manual trip logs. Dispatch technicians from a drag-and-drop board. Track them with GPS. Let them capture photos, checklists, and customer signatures on site. Close out jobs and generate invoices automatically. Your office sees everything in real time.",
    gallery: [
      { src: "/screenshots/dark-jobcards.png", label: "Job cards — dark mode", mode: "dark" },
      { src: "/screenshots/light-jobcards.png", label: "Job cards — light mode", mode: "light" },
      { src: "/screenshots/dark-scheduler.png", label: "Scheduling board", mode: "dark" },
      { src: "/screenshots/light-scheduler.png", label: "Scheduler — light mode", mode: "light" },
      { src: "/screenshots/dark-equipment.png", label: "Equipment tracking", mode: "dark" },
      { src: "/screenshots/dark-gallery.png", label: "Photo gallery", mode: "dark" },
      { src: "/screenshots/light-gallery.png", label: "Gallery — light mode", mode: "light" },
      { src: "/screenshots/dark-calendar.png", label: "Team calendar", mode: "dark" },
      { src: "/screenshots/dark-map.png", label: "GPS tracking map", mode: "dark" },
      { src: "/screenshots/mobile-jobcards.png", label: "Job cards — mobile", mode: "mobile" },
      { src: "/screenshots/mobile-map.png", label: "Map — mobile", mode: "mobile" },
    ],
    features: [
      {
        badge: "Job Cards",
        title: "Digital job cards that replace your entire paper stack",
        description: "Create job cards from templates, from leads, or from scratch. Assign to technicians, set priorities and SLAs, and track status in real time. Technicians complete jobs from their phones — capturing photos, filling checklists, adding notes, and collecting customer signatures before marking complete.",
        bullets: [
          "Job card lifecycle: Draft → Scheduled → In Progress → Complete → Invoiced",
          "Photo capture with before/after comparison and auto-organisation",
          "Checklist templates per job type with completion tracking",
          "Customer digital signatures captured on mobile",
          "Document attachments (manuals, diagrams, safety sheets)",
          "Direct conversion from support tickets or sales leads",
          "Customer portal visibility — clients track their job in real time",
        ],
        screenshots: [
          { src: "/screenshots/dark-jobcards.png", caption: "Job cards" },
          { src: "/screenshots/mobile-jobcards.png", caption: "Mobile", mobile: true },
        ],
      },
      {
        badge: "Completion Flow",
        title: "From arrival to invoice — a guided flow your technicians actually follow",
        description: "Every job card walks the technician through a structured completion sequence: clock-in (GPS-verified), site arrival, before-photos, checklist, work notes, after-photos, customer signature, and submit. Each step is required, time-stamped, and synced offline-first. The office sees real-time progress; the customer gets an automated update when the work is signed off.",
        bullets: [
          "Step-by-step guided completion flow on mobile",
          "GPS-verified clock-in and site-arrival timestamps",
          "Before/after photo capture with auto-organisation",
          "Per-job-type checklists with mandatory items",
          "Voice-to-text work notes refined by Cerebrum",
          "Customer signature capture on-device",
          "Offline-first — syncs the second connectivity returns",
          "Auto-generates invoice the moment the job is submitted",
        ],
        useVisual: "jobcard-flow",
      },
      {
        badge: "Efficiency Tracking",
        title: "See exactly how long each job takes — and where the time really goes",
        description: "Every clock-in, drive, on-site duration, and admin minute is tracked automatically. Compare actual time-on-site against quoted time. Spot technicians who are crushing it. Spot job types that are consistently underquoted. Cerebrum surfaces the patterns so you can re-estimate, re-train, or re-route accordingly.",
        bullets: [
          "Time-per-job, time-per-job-type, time-per-technician analytics",
          "Quoted-vs-actual time variance with margin impact",
          "Drive time vs. on-site time breakdown",
          "Technician efficiency leaderboards (with anti-burnout flags)",
          "Cerebrum-flagged underquoted job types for re-pricing",
          "Weekly efficiency digest for ops managers",
        ],
        screenshots: [
          { src: "/screenshots/dark-analytics.png", caption: "Efficiency analytics" },
          { src: "/screenshots/light-analytics.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Scheduling Board",
        title: "Drag-and-drop scheduling with conflict detection and workload balancing",
        description: "See your entire team's week in one view. Drag jobs onto technician time slots. The scheduler flags conflicts, balances workloads, and syncs to each technician's mobile in real time. Google Calendar integration keeps everything aligned.",
        bullets: [
          "Drag-and-drop weekly and monthly scheduling board",
          "Technician availability and workload balancing",
          "Conflict detection and overbooking alerts",
          "Google Calendar two-way sync",
          "Vehicle assignment and fleet conflict detection",
        ],
        screenshots: [
          { src: "/screenshots/dark-scheduler.png", caption: "Scheduling board" },
          { src: "/screenshots/light-scheduler.png", caption: "Light mode" },
        ],
      },
      {
        badge: "GPS & Maps",
        title: "Track your field team in real time — navigate to any customer in one tap",
        description: "Every clock-in, job arrival, and departure is GPS-stamped. Dispatchers see team locations on a live map. Technicians get one-tap navigation to customer addresses with verified coordinates from Google Places.",
        bullets: [
          "GPS-verified clock-in and job arrival/departure",
          "Live team location map for dispatchers",
          "One-tap navigation to customer sites",
          "Address resolution via Google Places and Plus Codes",
          "Route history and drive time logging",
        ],
        screenshots: [
          { src: "/screenshots/dark-map.png", caption: "GPS tracking map" },
          { src: "/screenshots/mobile-map.png", caption: "Mobile", mobile: true },
        ],
      },
      {
        badge: "Equipment & Fleet",
        title: "Track every asset — vehicles, tools, and serial-numbered parts",
        description: "Maintain a complete asset register with serial numbers, photos, service history, and assignment tracking. Link equipment to job cards so you always know what went on site. Vehicle management with conflict detection prevents double-booking your fleet.",
        bullets: [
          "Asset register with serial numbers, photos, and specifications",
          "Assignment tracking per technician with handover logs",
          "Service history and preventive maintenance scheduling",
          "Vehicle fleet management with availability tracking",
          "Vehicle-to-job conflict detection",
          "QR code scanning for instant asset lookups",
        ],
        screenshots: [
          { src: "/screenshots/dark-equipment.png", caption: "Equipment" },
        ],
      },
      {
        badge: "Photo Gallery",
        title: "Every photo, every job, organised and searchable automatically",
        description: "Photos taken on job cards are auto-organised by customer, job, and date. AI-powered image description makes every photo searchable by content. Create folders, share galleries via the customer portal, and generate proof-of-work reports with a click.",
        bullets: [
          "Auto-organised by job card, customer, and date",
          "AI-powered image description and search (Vision API)",
          "Custom folders for site audits and inspections",
          "Customer portal sharing — clients see their job photos",
          "Bulk upload with offline sync from mobile",
          "Before/after photo comparison for quality assurance",
        ],
        screenshots: [
          { src: "/screenshots/dark-gallery.png", caption: "Photo gallery" },
          { src: "/screenshots/light-gallery.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Customer Sites",
        title: "Multi-site customer management with GPS-verified addresses",
        description: "Customers with multiple locations? Each site gets its own record with address, contacts, equipment, and job history. GPS-verified coordinates ensure your technicians never show up at the wrong gate.",
        bullets: [
          "Multi-site management per customer",
          "GPS-verified addresses with coordinate resolution",
          "Site-specific equipment and maintenance history",
          "Site contacts and access instructions",
        ],
        screenshots: [
          { src: "/screenshots/dark-customers.png", caption: "Customer management" },
        ],
      },
    ],
    cerebrum: "Cerebrum reads job card notes and auto-suggests equipment needed for each job type. It flags scheduling conflicts before your team leaves the office, generates completion reports from photos and notes automatically, detects recurring issues across similar jobs, and drafts customer communications when jobs are completed or delayed.",
    crossSell: ["sales-flow", "teams", "stockroom"],
    pricing: { starter: 129, pro: 199, business: 249 },
  },

  teams: {
    name: "Rogue Teams",
    tagline: "Time and attendance, tasks, chat, recruitment, roles, and HR — your entire workforce in one tab.",
    icon: Users,
    heroDescription: "Teams gives you complete visibility into your workforce — from hiring to daily operations. GPS-fenced clock-in eliminates buddy-clocking. Task management tracks every assignment. Internal chat keeps conversations contextual. Role-based permissions control who sees what. And when you need to grow the team, Recruitment is built right in: AI-matched candidates, digital onboarding, and employment document generation.",
    gallery: [
      { src: "/screenshots/dark-tasks.png", label: "Task management — dark mode", mode: "dark" },
      { src: "/screenshots/light-tasks.png", label: "Tasks — light mode", mode: "light" },
      { src: "/screenshots/dark-team.png", label: "Team management", mode: "dark" },
      { src: "/screenshots/light-team.png", label: "Team — light mode", mode: "light" },
      { src: "/screenshots/dark-calendar.png", label: "Team calendar", mode: "dark" },
      { src: "/screenshots/light-calendar.png", label: "Calendar — light mode", mode: "light" },
      { src: "/screenshots/dark-chat.png", label: "Team chat", mode: "dark" },
      { src: "/screenshots/light-chat.png", label: "Chat — light mode", mode: "light" },
      { src: "/screenshots/dark-roles.png", label: "Role & permission editor", mode: "dark" },
      { src: "/screenshots/dark-onboarding.png", label: "Recruitment onboarding flow", mode: "dark" },
      { src: "/screenshots/dark-portal.png", label: "Candidate portal", mode: "dark" },
      { src: "/screenshots/dark-inbox.png", label: "Notification inbox", mode: "dark" },
      { src: "/screenshots/light-inbox.png", label: "Inbox — light mode", mode: "light" },
      { src: "/screenshots/mobile-tasks.png", label: "Tasks — mobile", mode: "mobile" },
      { src: "/screenshots/mobile-dashboard.png", label: "Dashboard — mobile", mode: "mobile" },
    ],
    features: [
      {
        badge: "Time & Attendance",
        title: "GPS-fenced clock-in — no more buddy-clocking, no more excuses",
        description: "Kiosk mode for office staff, mobile clock-in with GPS verification for field teams. Every clock-in records location coordinates and timestamps. Late arrivals, early departures, and no-shows are flagged instantly. Overtime is calculated automatically.",
        bullets: [
          "Kiosk mode for office-based clocking",
          "Mobile clock-in with GPS coordinate verification",
          "Late arrival and early departure alerts",
          "No-show detection and automatic escalation",
          "Overtime calculation and tracking",
          "Leave management and approval workflows",
          "Weekly and monthly attendance reports",
        ],
        screenshots: [
          { src: "/screenshots/dark-team.png", caption: "Team management" },
          { src: "/screenshots/light-team.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Task Management",
        title: "Every task assigned, tracked, and accountable — across all modules",
        description: "Create tasks from anywhere: job cards, leads, meetings, tickets, or standalone. Assign to individuals or teams, set priorities and due dates. Tasks can be transferred, rejected with reasons, or completed with notes. Drag-and-drop reordering lets each team member prioritise their own board.",
        bullets: [
          "Task creation from any module (job cards, leads, tickets, meetings)",
          "Assignment to individuals or teams with notifications",
          "Priority levels (urgent, high, medium, low) with visual indicators",
          "Due date tracking with overdue escalation",
          "Task transfer and rejection with audit trail",
          "Note threads on each task for context",
          "Drag-and-drop reordering per user",
        ],
        screenshots: [
          { src: "/screenshots/dark-tasks.png", caption: "Task board" },
          { src: "/screenshots/light-tasks.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Team Chat",
        title: "Internal messaging that keeps conversations linked to your work",
        description: "No more lost WhatsApp messages. Rogue's chat links conversations to customers, jobs, projects, and tickets. Create threads for any entity, share files, tag colleagues, and search everything later. Audio messages are auto-transcribed by AI.",
        bullets: [
          "Entity-linked threads (per customer, job, project, ticket)",
          "File sharing and @mentions",
          "Audio message recording with AI transcription",
          "Searchable message history across all threads",
          "Push notifications on mobile and desktop",
          "Thread archiving for completed entities",
        ],
        screenshots: [
          { src: "/screenshots/dark-chat.png", caption: "Team chat" },
          { src: "/screenshots/light-chat.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Recruitment",
        title: "Hire, match, and onboard — built right into your team management",
        description: "When you need to grow, Recruitment lives inside Teams. Post roles and let Cerebrum score applicants against your requirements. Track candidates through interview stages. Onboard new hires with digital checklists, document uploads, and e-signatures — all completed on their phone before day one.",
        bullets: [
          "AI-powered candidate scoring against role requirements",
          "Application tracking pipeline (Applied → Screening → Interview → Offer → Hired)",
          "Public application forms with custom fields",
          "Digital onboarding checklists with progress tracking",
          "Document upload (ID, qualifications, certificates) with e-signatures",
          "Employment contract generation from templates",
          "Connects directly to Roles & Permissions for new user setup",
        ],
        screenshots: [
          { src: "/screenshots/dark-onboarding.png", caption: "Recruitment & onboarding" },
          { src: "/screenshots/dark-portal.png", caption: "Candidate portal" },
        ],
      },
      {
        badge: "Configurable Leaderboards",
        title: "Pick the metric, pick the window — your business decides what 'winning' means",
        description: "Every Rogue tenant builds its own leaderboards. Rank technicians by job cards completed, overtime hours, photo compliance, average customer rating, ticket resolution speed, sales won — whatever moves your numbers. Switch the window (last 7 / 30 / 90 / 365 days) and the bars re-rank instantly. The same horizontal-bar widget powers Sales (rep leaderboard), Field (technician utilisation, photo compliance, ratings), Support (resolution speed), and Teams.",
        bullets: [
          "Configurable metric per leaderboard — not a fixed format",
          "Built-in metrics: job cards completed, overtime hours, photo compliance, customer ratings, on-time clock-ins, ticket resolutions, sales wins",
          "Date-range pills: 7d / 30d / 90d / 180d / 365d (same control everywhere in Analytics Hub)",
          "Role-gated — owner/admin/manager/rep only; clients never see internal leaderboards",
          "Pin leaderboards to the office TV, the manager dashboard, or keep them private",
          "Custom-metric definitions for industry-specific KPIs",
          "Anti-gaming detection (Cerebrum flags suspicious activity spikes)",
        ],
        useVisual: "leaderboard",
      },
      {
        badge: "Badges, Streaks & Recognition",
        title: "Auto-awarded recognition that lifts retention more than a raise",
        description: "Cerebrum watches the entire team's behaviour and hands out badges automatically each week: early-birds, perfect attendance, most-improved, helping-hand, gentle-nudges for late arrivals. Badges are published directly to team chat, so the right people get the right shout-out — without a manager having to remember.",
        bullets: [
          "Auto-awarded weekly badges based on real activity data",
          "Punctuality streaks (consecutive on-time days) tracked per person",
          "Positive recognition: Early Bird, Perfect Attendance, Most Improved, Helping Hand, Cerebrum's Pick",
          "Gentle nudges (not public shaming) for late arrivals — surfaced privately to managers with coaching prompts",
          "Custom award definitions per team or department",
          "Badges published to team chat with auto-generated congratulatory messages",
          "Per-person achievement gallery — career growth visualised",
        ],
        useVisual: "badges-rewards",
      },
      {
        badge: "AI CV Extraction & Role Analysis",
        title: "Drop a CV. Cerebrum reads, extracts, and scores against the role in seconds",
        description: "When a candidate uploads a CV (PDF, DOCX, or image), Cerebrum extracts experience, qualifications, certifications, and contact details — then scores the candidate against the role's requirements with explainable confidence. Skill-by-skill match, gap analysis, and recommended interview questions are generated automatically.",
        bullets: [
          "Multi-format CV parsing (PDF, DOCX, image, scanned)",
          "Structured extraction: experience, education, certifications, contact, references",
          "Role-match scoring with skill-by-skill breakdown",
          "Gap analysis — what the candidate is missing for the role",
          "Auto-generated interview question pack tailored to the candidate",
          "Comparison view across all applicants for the role",
          "Bias-aware mode: scores blind to name, gender, age, photo",
        ],
        useVisual: "cv-extraction",
      },
      {
        badge: "Roles & Permissions",
        title: "Control exactly who sees what — from owner to field technician",
        description: "Rogue's RBAC system lets you create custom roles with granular permissions. Control access to navigation items, actions (create, edit, delete), and data visibility. Technicians see only their jobs. Managers see their team. Owners see everything.",
        bullets: [
          "Custom role creation with granular permission toggles",
          "Pre-built roles: Owner, Admin, Manager, Rep, Technician, Support Officer",
          "Navigation-level access control (hide entire modules per role)",
          "Action-level permissions (create, edit, delete, approve)",
          "User management with role assignment and password resets",
        ],
        screenshots: [
          { src: "/screenshots/dark-roles.png", caption: "Role & permission editor" },
        ],
      },
      {
        badge: "Notifications & Bulletins",
        title: "Push announcements to your team and track who's seen what",
        description: "Team bulletins with scheduling, targeting, and read receipts. Push notifications for urgent alerts. The awareness system tracks who has seen new leads, tickets, and job cards — flagging unseen items with a rose dot so nothing slips through.",
        bullets: [
          "Team bulletins with schedule windows and targeting",
          "Web push notifications for mobile and desktop",
          "Awareness tracking (unseen dot system) across all lists",
          "Notification inbox with read/unread management",
          "Dismissible alerts for completed actions",
        ],
        screenshots: [
          { src: "/screenshots/dark-inbox.png", caption: "Notification inbox" },
          { src: "/screenshots/light-inbox.png", caption: "Light mode" },
        ],
      },
    ],
    cerebrum: "Cerebrum powers Teams at every level: it scores recruitment candidates with multi-factor matching, detects attendance anomalies and burnout risk, suggests optimal task assignments based on workload and skill, auto-transcribes voice messages in chat, builds per-user coaching profiles from real performance data, and generates weekly team summaries for managers.",
    crossSell: ["ops", "projects", "support"],
    pricing: { starter: 79, pro: 119, business: 149 },
  },

  stockroom: {
    name: "Rogue Stockroom",
    tagline: "Inventory, suppliers, procurement, OCR scanning, and datasheets — your entire supply chain in one module.",
    icon: Package,
    heroDescription: "Stockroom gives you real-time visibility into every item across every location. Scan supplier invoices and price-lists with AI-powered OCR. Track stock levels with automated alerts. Manage supplier relationships with performance scoring. Plan procurement based on consumption patterns and project needs. Link inventory to job cards so you always know what's been used where.",
    gallery: [
      { src: "/screenshots/dark-suppliers.png", label: "Suppliers — dark mode", mode: "dark" },
      { src: "/screenshots/light-suppliers.png", label: "Suppliers — light mode", mode: "light" },
      { src: "/screenshots/dark-equipment.png", label: "Inventory tracking", mode: "dark" },
      { src: "/screenshots/dark-procurement.png", label: "Procurement planning", mode: "dark" },
      { src: "/screenshots/dark-ocr.png", label: "OCR invoice scanning", mode: "dark" },
      { src: "/screenshots/dark-orders.png", label: "Order management", mode: "dark" },
      { src: "/screenshots/dark-gallery.png", label: "Product documentation", mode: "dark" },
    ],
    features: [
      {
        badge: "Inventory Management",
        title: "Track every item across every location — with categories, datasheets, and auto-alerts",
        description: "Maintain a complete inventory register with quantities, locations, minimum stock levels, and category classification. AI auto-categorises new items. Link inventory to technical datasheets for specifications and manufacturer information. Get alerted when stock drops below thresholds.",
        bullets: [
          "Multi-location inventory tracking with real-time quantities",
          "Custom category taxonomy with AI auto-classification",
          "Minimum stock alerts and automated reorder triggers",
          "Technical datasheet linking per item (manufacturer, specs, certifications)",
          "Inventory-to-job-card usage tracking",
          "Stock-take workflows with variance reporting",
          "Barcode and QR code scanning for instant lookups",
        ],
        screenshots: [
          { src: "/screenshots/dark-equipment.png", caption: "Inventory" },
        ],
      },
      {
        badge: "Supplier Management",
        title: "Every supplier relationship tracked — contacts, orders, documents, and performance",
        description: "Build a comprehensive supplier database with contacts, pricing agreements, delivery terms, and document storage. Track order history, delivery performance, and pricing trends. Supplier 360 view shows everything in one place — inventory supplied, open orders, documents, and performance scoring.",
        bullets: [
          "Supplier database with contacts, addresses, and terms",
          "Document management (contracts, certifications, price-lists)",
          "Order history and delivery tracking",
          "Supplier performance scoring and comparison",
          "Supplier 360 view — inventory, orders, documents in one screen",
          "Inventory-to-supplier linking for quick reordering",
        ],
        screenshots: [
          { src: "/screenshots/dark-suppliers.png", caption: "Suppliers" },
          { src: "/screenshots/light-suppliers.png", caption: "Light mode" },
        ],
      },
      {
        badge: "OCR & AI Scanning",
        title: "Scan invoices and price-lists — AI extracts every line item automatically",
        description: "Take a photo of a supplier invoice or price-list and Cerebrum extracts every line item, quantity, price, and total. Matched against existing inventory for instant stock updates. Pricing anomalies are flagged before you pay.",
        bullets: [
          "AI-powered invoice OCR (photo → structured data)",
          "Price-list scanning with automatic line-item extraction",
          "Automatic matching to existing inventory items",
          "Pricing anomaly detection across vendors",
          "Bulk import from CSV, XLSX, or PDF",
        ],
        screenshots: [
          { src: "/screenshots/dark-ocr.png", caption: "OCR scanning" },
        ],
      },
      {
        badge: "Procurement",
        title: "Plan purchases based on consumption, project needs, and supplier lead times",
        description: "The procurement planner analyses stock consumption patterns, upcoming project requirements, and supplier lead times to generate purchase recommendations. Create purchase orders, track delivery, and manage the full procurement cycle from request to receipt.",
        bullets: [
          "AI-powered consumption analysis and demand forecasting",
          "Purchase order generation with supplier selection",
          "Procurement radar per project (what's needed, when, from whom)",
          "Delivery tracking and receipt confirmation",
          "Budget allocation and spend tracking per project",
        ],
        screenshots: [
          { src: "/screenshots/dark-procurement.png", caption: "Procurement planner" },
          { src: "/screenshots/dark-orders.png", caption: "Order management" },
        ],
      },
      {
        badge: "Technical Datasheets",
        title: "Store and search technical specifications with AI-powered metadata extraction",
        description: "Upload product datasheets (PDF, images) and Cerebrum auto-extracts manufacturer, model, specifications, and certifications. Search across all datasheets by specification, manufacturer, or application. Link datasheets to inventory items for instant spec lookups on site.",
        bullets: [
          "PDF and image datasheet upload with AI metadata extraction",
          "Manufacturer and model auto-detection",
          "Full-text search across specifications",
          "Linked to inventory items for on-site spec lookups",
          "Category-based organisation and filtering",
        ],
        screenshots: [
          { src: "/screenshots/dark-gallery.png", caption: "Documentation" },
        ],
      },
    ],
    cerebrum: "Cerebrum powers Stockroom's intelligence: OCR reads invoices and price-lists in seconds, auto-categorises inventory items, predicts stock-outs based on consumption velocity, flags pricing anomalies across vendors, extracts metadata from technical datasheets, and generates procurement recommendations for upcoming projects.",
    crossSell: ["ops", "bookkeep", "sales-flow"],
    pricing: { starter: 99, pro: 149, business: 199 },
  },

  bookkeep: {
    name: "Rogue Bookkeep",
    tagline: "Expenses, invoices, statements, and profitability analytics — bookkeeping that manages itself.",
    icon: BookOpen,
    heroDescription: "Bookkeep tracks every rand in and out of your business. OCR scans receipts and auto-categorises expenses. Generate invoices and statements from customer records. Track payment status with automated reminders. Analytics dashboards explain your profitability in plain language — by customer, by project, by product line.",
    gallery: [
      { src: "/screenshots/dark-expense-review.png", label: "Expense review — dark mode", mode: "dark" },
      { src: "/screenshots/light-expense-review.png", label: "Expenses — light mode", mode: "light" },
      { src: "/screenshots/dark-analytics.png", label: "Profitability analytics", mode: "dark" },
      { src: "/screenshots/light-analytics.png", label: "Analytics — light mode", mode: "light" },
      { src: "/screenshots/dark-suppliers.png", label: "Supplier spend tracking", mode: "dark" },
      { src: "/screenshots/light-suppliers.png", label: "Suppliers — light mode", mode: "light" },
    ],
    features: [
      {
        badge: "Expenses & OCR",
        title: "Scan receipts, auto-categorise expenses, and track every rand against budgets",
        description: "Photograph a receipt and Cerebrum extracts vendor, amount, date, and category. Expenses are automatically linked to projects and suppliers. The expense review queue lets managers approve or reject before costs hit the budget. Threshold alerts fire when a project approaches its budget limit.",
        bullets: [
          "Receipt OCR: photo → vendor, amount, date, category",
          "Automatic supplier matching from receipt data",
          "Category auto-assignment with AI classification",
          "Expense-to-project linking for cost tracking",
          "Manager approval queue with bulk approve/reject",
          "Budget threshold alerts per project",
          "Exportable expense reports for accounting",
        ],
        screenshots: [
          { src: "/screenshots/dark-expense-review.png", caption: "Expense review queue" },
          { src: "/screenshots/light-expense-review.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Invoicing & Statements",
        title: "Generate invoices, track payments, and send reminders — all connected to your customers",
        description: "Create professional invoices directly from customer records, quotes, or job cards. Track payment status (pending, paid, overdue). Generate customer statements showing full transaction history. Automated payment reminders keep cash flow healthy.",
        bullets: [
          "Invoice generation from customer records, quotes, or job cards",
          "Payment status tracking with automated reminders",
          "Customer statements with full transaction history",
          "PDF export for email or print",
          "Customer portal visibility — clients view and pay invoices online",
          "Linked to sales documents for full audit trail",
        ],
        screenshots: [
          { src: "/screenshots/dark-customers.png", caption: "Customer invoicing" },
          { src: "/screenshots/light-customers.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Analytics & Reporting",
        title: "Revenue, margin, and profitability analytics — annotated by Cerebrum",
        description: "Drill into revenue by customer, project, or product line. See expense breakdowns, margin trends, and profitability comparisons. Cerebrum annotates dashboards with plain-language insights: why revenue dipped last week, which project is burning faster than expected, and what your team should focus on next.",
        bullets: [
          "Revenue and expense dashboards with custom date ranges",
          "Customer-level revenue and margin analysis",
          "Supplier spend tracking and comparison",
          "Cerebrum-annotated insights and anomaly alerts",
          "Exportable reports for board meetings and accounting",
          "VAT and tax report exports",
        ],
        screenshots: [
          { src: "/screenshots/dark-analytics.png", caption: "Analytics" },
          { src: "/screenshots/light-analytics.png", caption: "Light mode" },
        ],
      },
    ],
    cerebrum: "Cerebrum powers Bookkeep's intelligence: it categorises expenses automatically from receipt scans, flags cost anomalies before they become problems, matches receipts to suppliers automatically, predicts cash flow based on invoice aging, and explains profitability trends in plain language for non-finance team members.",
    crossSell: ["projects", "stockroom", "sales-flow"],
    pricing: { starter: 99, pro: 149, business: 199 },
  },

  support: {
    name: "Rogue Support",
    tagline: "Tickets, SLAs, customer portal, and AI-drafted replies — support that scales without headcount.",
    icon: HeadphonesIcon,
    heroDescription: "Support turns reactive customer service into a proactive retention engine. Manage tickets with SLA tracking and auto-escalation. Give customers a branded self-service portal where they see jobs, tickets, invoices, and photos. Let Cerebrum draft the first response to every ticket. Convert resolved tickets into job cards when on-site work is needed.",
    gallery: [
      { src: "/screenshots/dark-tickets.png", label: "Ticket management — dark mode", mode: "dark" },
      { src: "/screenshots/light-tickets.png", label: "Tickets — light mode", mode: "light" },
      { src: "/screenshots/dark-customers.png", label: "Customer 360", mode: "dark" },
      { src: "/screenshots/light-customers.png", label: "Customers — light mode", mode: "light" },
      { src: "/screenshots/mobile-tickets.png", label: "Tickets — mobile", mode: "mobile" },
      { src: "/screenshots/mobile-customers.png", label: "Customers — mobile", mode: "mobile" },
    ],
    features: [
      {
        badge: "Ticket Management",
        title: "Every issue tracked from report to resolution — with SLA enforcement",
        description: "Create tickets from the customer portal, email, or internally. Priority levels and SLA deadlines ensure nothing is forgotten. Auto-assignment rules route tickets to the right team. Track first-response time, resolution time, and customer satisfaction. Tickets link to customer records, job cards, and chat threads for full context.",
        bullets: [
          "Priority levels: Critical, High, Medium, Low with visual indicators",
          "SLA deadline tracking with auto-escalation",
          "Auto-assignment rules by category, priority, or team",
          "First-response and resolution time tracking",
          "Note threads with internal/external visibility control",
          "File and photo attachments per ticket",
          "Ticket → Job Card conversion for on-site resolution",
          "Ticket → Chat thread for extended discussions",
        ],
        screenshots: [
          { src: "/screenshots/dark-tickets.png", caption: "Ticket management" },
          { src: "/screenshots/light-tickets.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Customer Portal",
        title: "Give customers a branded window into their service — without the phone calls",
        description: "Customers log into a branded portal (your logo, your domain) and see everything: open tickets, job card progress, invoices, quotes, photos, and documents. They can submit new tickets, approve quotes, place orders, and communicate with your team — all self-service.",
        bullets: [
          "Branded portal with your logo and custom domain",
          "Self-service ticket creation and status tracking",
          "Job card progress visibility with photos and status",
          "Invoice and quote viewing with approval workflows",
          "Document sharing with e-signature support",
          "Order placement through portal storefront",
          "Multi-user portal accounts per customer (with team permissions)",
          "Contact management from the customer side",
        ],
        screenshots: [
          { src: "/screenshots/dark-customers.png", caption: "Customer 360" },
          { src: "/screenshots/mobile-customers.png", caption: "Mobile", mobile: true },
        ],
      },
      {
        badge: "AI-Drafted Replies",
        title: "Cerebrum writes the first draft — your team refines and sends in seconds",
        description: "Every ticket gets a Cerebrum-drafted first response based on the issue description, past resolutions for similar tickets, and customer history. Support agents review, edit if needed, and send. Response times drop dramatically without sacrificing quality.",
        bullets: [
          "AI-drafted first response for every ticket",
          "Context-aware: reads full ticket history and customer records",
          "Learns from edits to improve future drafts",
          "Auto-escalation when SLA deadlines approach",
          "Suggested knowledge base articles for common issues",
          "Recurring issue detection and flagging for product team",
        ],
        screenshots: [
          { src: "/screenshots/dark-tickets.png", caption: "AI-assisted tickets" },
        ],
      },
    ],
    cerebrum: "Cerebrum drafts the first response to every ticket based on issue description, customer history, and past resolution patterns. It auto-escalates approaching SLA deadlines, suggests knowledge base articles to customers, detects recurring issues for your product team, and summarises weekly support metrics with actionable recommendations.",
    crossSell: ["ops", "teams", "sales-flow"],
    pricing: { starter: 79, pro: 119, business: 149 },
  },

  cerebrum: {
    name: "Rogue Cerebrum",
    tagline: "The AI that lives inside every module — researching, drafting, alerting, and learning from your business.",
    icon: Brain,
    heroDescription: "Cerebrum isn't a chatbot bolted onto the side. It's the intelligence layer woven into every screen in Rogue. It researches leads from 6+ data sources. Drafts ticket replies in your team's voice. Scans invoices with OCR. Detects scheduling conflicts. Predicts stock-outs. Coaches your team. And it learns your business patterns to get smarter every week. White-label it as your own AI brand for your customers.",
    gallery: [
      { src: "/screenshots/dark-analytics.png", label: "AI analytics — dark mode", mode: "dark" },
      { src: "/screenshots/light-analytics.png", label: "Analytics — light mode", mode: "light" },
      { src: "/screenshots/dark-research.png", label: "AI deep research", mode: "dark" },
      { src: "/screenshots/dark-salesfunnel.png", label: "AI sales insights", mode: "dark" },
      { src: "/screenshots/dark-engineering.png", label: "AI engineering desk", mode: "dark" },
      { src: "/screenshots/dark-dashboard.png", label: "AI dashboard", mode: "dark" },
      { src: "/screenshots/light-dashboard.png", label: "Dashboard — light mode", mode: "light" },
    ],
    features: [
      {
        badge: "Cross-Module Intelligence",
        title: "One brain that sees patterns across your entire operation",
        description: "Cerebrum connects data across SalesFlow, Ops, Teams, Bookkeep, Support, and Stockroom. It spots patterns no single module could: a customer whose support tickets are spiking while invoices go unpaid, a technician whose completion times are increasing while photo quality drops, a supplier whose pricing is drifting above market.",
        bullets: [
          "Cross-module data correlation and pattern detection",
          "Proactive risk alerts across your entire operation",
          "Natural-language queries: 'Which customers haven't ordered in 60 days?'",
          "Automated weekly insight digests for managers",
          "Activity-based performance profiles per team member",
          "Custom AI tools that execute actions (role changes, data lookups, calculations)",
        ],
        screenshots: [
          { src: "/screenshots/dark-analytics.png", caption: "Cross-module analytics" },
          { src: "/screenshots/light-analytics.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Voice & Natural Language",
        title: "Talk to Cerebrum — it transcribes, refines, and executes",
        description: "Cerebrum understands voice commands and natural-language queries. Dictate job card notes and they're auto-cleaned and structured. Ask questions about your data in plain English. Cerebrum transcribes audio messages in chat and refines rough voice notes into professional text.",
        bullets: [
          "Voice-to-text with AI refinement",
          "Natural-language data queries across all modules",
          "Audio message transcription in team chat",
          "Hands-free mode for field technicians",
          "Context-aware responses based on current screen",
        ],
        screenshots: [
          { src: "/screenshots/dark-dashboard.png", caption: "Voice interface" },
        ],
      },
      {
        badge: "AI Engineering Desk",
        title: "Technical problem-solving sessions with full context from your operation",
        description: "The engineering desk lets your team run structured AI sessions for complex problems — troubleshooting equipment failures, designing system configurations, or researching technical solutions. Cerebrum pulls context from job cards, datasheets, and equipment records to give informed technical answers. Sessions are saved as a searchable library.",
        bullets: [
          "Structured AI problem-solving sessions",
          "Context injection from job cards, equipment, and datasheets",
          "Session library with full-text search",
          "Category-based organisation (troubleshooting, design, research)",
          "PDF export for documentation and sharing",
          "Knowledge base that grows with every session",
        ],
        screenshots: [
          { src: "/screenshots/dark-engineering.png", caption: "AI engineering desk" },
        ],
      },
      {
        badge: "Self-Diagnosis & Memory",
        title: "Cerebrum monitors its own performance and learns from every interaction",
        description: "Cerebrum runs self-diagnosis checks to identify where it's underperforming. A feedback loop tracks every accept, edit, and reject so drafts improve over time. The memory system builds a playbook of what works for your business — win/loss patterns, successful follow-up templates, and coaching insights.",
        bullets: [
          "Self-diagnosis scheduler with automated health reports",
          "Feedback tracking: accept, edit, reject rates per context",
          "Playbook generation from successful patterns",
          "Win/loss reflection system for continuous improvement",
          "Memory profile per user with learned preferences",
          "Bug reporting with automatic context capture",
        ],
        screenshots: [
          { src: "/screenshots/dark-research.png", caption: "AI research system" },
        ],
      },
      {
        badge: "White-Label",
        title: "Rebrand Cerebrum as your own AI — your customers see your name",
        description: "Premium feature: rename Cerebrum to your brand's AI assistant. Your customers interact with your AI, not ours. Custom personality settings, tone of voice, and knowledge base — all configurable by you. Available on Pro and Business tiers.",
        bullets: [
          "Custom AI name and branding",
          "Personality and tone configuration",
          "Custom knowledge base per tenant",
          "Available on Pro and Business tiers",
          "Available on Pro and Business tiers",
        ],
        screenshots: [
          { src: "/screenshots/dark-salesfunnel.png", caption: "AI insights" },
        ],
      },
    ],
    cerebrum: "Cerebrum IS the module. It's the AI layer that connects every Rogue module. It learns your business patterns, gets smarter with every interaction, runs self-diagnostics to ensure quality, and can be white-labelled as your own AI brand. It's the reason Rogue is more than the sum of its parts.",
    crossSell: ["sales-flow", "ops", "support"],
    pricing: { starter: 99, pro: 149, business: 199 },
  },

  projects: {
    name: "Rogue Projects",
    tagline: "Full project management — Gantt timelines, budgets, risk registers, and real-time profitability tracking.",
    icon: FolderKanban,
    heroDescription: "Rogue Projects is a standalone project management module that rivals Monday, Asana, and Smartsheet — but directly connected to your job cards, expenses, invoices, and team schedules. Track every project from planning to completion with interactive Gantt charts, real-time budget vs. actual, milestone management, and AI-powered slippage detection. Know your margin before the project closes, not after.",
    gallery: [
      { src: "/screenshots/dark-projects.png", label: "Project dashboard — dark mode", mode: "dark" },
      { src: "/screenshots/light-projects.png", label: "Projects — light mode", mode: "light" },
      { src: "/screenshots/dark-timeline.png", label: "Gantt timeline", mode: "dark" },
      { src: "/screenshots/light-timeline.png", label: "Timeline — light mode", mode: "light" },
      { src: "/screenshots/dark-analytics.png", label: "Project analytics", mode: "dark" },
      { src: "/screenshots/light-analytics.png", label: "Analytics — light mode", mode: "light" },
      { src: "/screenshots/dark-calendar.png", label: "Project calendar", mode: "dark" },
    ],
    features: [
      {
        badge: "Project Dashboard",
        title: "Every project at a glance — budget, margin, milestones, and team",
        description: "The project dashboard shows all active projects with real-time budget tracking, spent vs. allocated, margin calculations, and phase status. Each project card surfaces the key metric that matters: is this project profitable, and is it on schedule? Click into any project for the full detail view.",
        bullets: [
          "Multi-project portfolio view with status indicators",
          "Real-time budget vs. actual spend tracking",
          "Margin calculation: revenue minus all linked expenses",
          "Phase management: Planning → Active → Review → Complete → Archived",
          "Milestone tracking with deadline alerts and completion %",
          "Team allocation view — who's assigned to what",
          "Project-level notes and document attachments",
        ],
        screenshots: [
          { src: "/screenshots/dark-projects.png", caption: "Project dashboard" },
          { src: "/screenshots/light-projects.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Gantt Timeline",
        title: "Interactive Gantt that actually connects to field operations",
        description: "Most project tools show pretty timelines disconnected from reality. Rogue's Gantt is connected to actual job cards, expenses, and field progress. When a job card is completed, the timeline updates. When an expense is logged, the budget bar adjusts. Drag tasks to reschedule, set dependencies, and track the critical path.",
        bullets: [
          "Interactive drag-and-drop Gantt chart",
          "Task dependencies with critical path visualisation",
          "Baseline comparison showing planned vs. actual progress",
          "Connected to job cards — field completions update the timeline",
          "Connected to expenses — costs reflect in real time",
          "Team workload view across all projects",
          "Filter by project, team member, date range, or status",
          "Automated slippage alerts sent to project managers",
        ],
        screenshots: [
          { src: "/screenshots/dark-timeline.png", caption: "Gantt timeline" },
          { src: "/screenshots/light-timeline.png", caption: "Light mode" },
        ],
        useVisual: "gantt",
      },
      {
        badge: "Budget & P&L",
        title: "Know your project profit before it closes — not three months later",
        description: "Every project has a live P&L statement. Revenue from invoices and quotes flows in. Expenses, labour costs, and procurement spend flow out. The margin is visible in real time, and Cerebrum flags projects at risk of going negative before it's too late.",
        bullets: [
          "Live profit & loss per project",
          "Revenue tracking from linked invoices and quotes",
          "Expense tracking with OCR receipt scanning",
          "Labour cost calculation from job card hours",
          "Procurement spend from linked purchase orders",
          "Margin alerts when projects approach break-even",
          "Exportable financial summaries per project",
        ],
        screenshots: [
          { src: "/screenshots/dark-analytics.png", caption: "Project P&L" },
          { src: "/screenshots/light-analytics.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Risk & Slippage",
        title: "Automated risk detection — Cerebrum watches your projects 24/7",
        description: "Cerebrum runs slippage detection on a scheduled basis, comparing actual progress against planned timelines. Risk registers track identified risks with mitigation plans. When a project is falling behind, alerts are pushed to managers before the client notices.",
        bullets: [
          "Automated slippage detection against baseline timelines",
          "Risk register with severity, likelihood, and mitigation tracking",
          "Scheduled monitoring with push notifications",
          "Procurement radar: what's needed, when, from which supplier",
          "Purchase order generation directly from project requirements",
          "Cross-project resource conflict detection",
        ],
        screenshots: [
          { src: "/screenshots/dark-procurement.png", caption: "Procurement radar" },
        ],
      },
    ],
    cerebrum: "Cerebrum monitors every project continuously: it detects timeline slippage against baselines, predicts margin outcomes based on current burn rates, generates procurement recommendations for upcoming phases, flags cross-project resource conflicts, and produces weekly project health summaries for managers and stakeholders.",
    crossSell: ["ops", "bookkeep", "stockroom"],
    pricing: { starter: 99, pro: 149, business: 199 },
  },

  connect: {
    name: "Rogue Connect",
    tagline: "Your branded customer portal — self-service tickets, job tracking, documents, orders, and communication.",
    icon: Link2,
    heroDescription: "Rogue Connect is a white-label customer portal that gives your clients a window into everything you do for them. Customers log in to track jobs, view invoices, submit tickets, approve quotes, place orders, share documents, and communicate with your team. It's your brand, your domain — they never see Rogue. Connect turns your customer relationships from phone-call-dependent to self-service-first.",
    gallery: [
      { src: "/screenshots/dark-customers.png", label: "Customer management — dark mode", mode: "dark" },
      { src: "/screenshots/light-customers.png", label: "Customers — light mode", mode: "light" },
      { src: "/screenshots/dark-tickets.png", label: "Portal ticket view", mode: "dark" },
      { src: "/screenshots/dark-orders.png", label: "Online ordering", mode: "dark" },
      { src: "/screenshots/dark-gallery.png", label: "Shared photo gallery", mode: "dark" },
      { src: "/screenshots/mobile-customers.png", label: "Portal — mobile", mode: "mobile" },
      { src: "/screenshots/mobile-tickets.png", label: "Tickets — mobile", mode: "mobile" },
    ],
    features: [
      {
        badge: "Self-Service Portal",
        title: "Your customers help themselves — your team handles exceptions, not routine requests",
        description: "Connect gives every customer a login to see their full account: open and completed job cards, invoices, quotes, support tickets, documents, and photos. They can submit new tickets, approve quotes, and check job status without calling your office. Multi-user accounts let their entire team participate.",
        bullets: [
          "Branded login portal (your logo, your domain, your colours)",
          "Job card tracking with real-time status and photo gallery",
          "Invoice and quote viewing with approval workflows",
          "Support ticket submission and tracking",
          "Document sharing with e-signatures",
          "Multi-user accounts per customer (with permission control)",
          "Contact management from the customer side",
          "Mobile-responsive — works on any device",
        ],
        screenshots: [
          { src: "/screenshots/dark-customers.png", caption: "Customer portal management" },
          { src: "/screenshots/mobile-customers.png", caption: "Mobile", mobile: true },
        ],
      },
      {
        badge: "Online Ordering",
        title: "Let customers place orders directly through the portal storefront",
        description: "Set up a storefront within Connect where customers browse your products and services, add items to a cart, and place orders. Orders flow directly into your Rogue pipeline — no manual data entry, no email chains, no lost POs.",
        bullets: [
          "Product/service catalogue within the portal",
          "Cart and checkout workflow",
          "Order tracking from placement to delivery",
          "Linked to inventory for real-time availability",
          "Order history and reorder functionality",
          "Custom pricing per customer or customer tier",
        ],
        screenshots: [
          { src: "/screenshots/dark-orders.png", caption: "Order management" },
        ],
      },
      {
        badge: "Document Hub",
        title: "Share contracts, certificates, and reports — securely and with full audit trail",
        description: "Upload documents to customer records and they appear in the portal instantly. Customers can upload their own documents too — purchase orders, site access forms, compliance certificates. Every document has version tracking and an audit trail of who viewed it and when.",
        bullets: [
          "Two-way document sharing (your team + customer)",
          "Version control and audit trail",
          "E-signature collection on contracts and approvals",
          "Document categories (contracts, certificates, invoices, reports)",
          "Bulk download and print-ready PDFs",
          "Secure access — each customer sees only their documents",
        ],
        screenshots: [
          { src: "/screenshots/dark-gallery.png", caption: "Document hub" },
          { src: "/screenshots/light-customers.png", caption: "Light mode" },
        ],
      },
      {
        badge: "Communication",
        title: "Push notifications, email, and in-portal messaging — keep customers informed without the phone",
        description: "Connect integrates with Rogue's notification system to push updates to customers automatically: job status changes, invoice reminders, ticket updates, and custom marketing messages. In-portal messaging keeps all customer communication in one searchable thread.",
        bullets: [
          "Automated notifications on job status changes",
          "Invoice and payment reminders",
          "Custom bulletin broadcasts to customer segments",
          "In-portal chat threads linked to tickets and jobs",
          "Push notifications on mobile devices",
          "Marketing messages and service announcements",
        ],
        screenshots: [
          { src: "/screenshots/dark-tickets.png", caption: "Customer communication" },
          { src: "/screenshots/mobile-tickets.png", caption: "Mobile", mobile: true },
        ],
      },
    ],
    cerebrum: "Cerebrum enhances Connect by auto-drafting customer communications, generating status update summaries, suggesting relevant documents to share based on job context, detecting at-risk customer accounts from portal activity patterns, and personalising the portal experience based on customer industry and usage.",
    crossSell: ["support", "sales-flow", "ops"],
    pricing: { starter: 79, pro: 119, business: 149 },
  },
};
